import { head } from "lodash";
import GoogleMapsLoader from "@sparefoot/google-maps";

let googleLoadPromise;
const elements = [];

export function initializeGoogle({ key }) {
	GoogleMapsLoader.KEY = key;
	GoogleMapsLoader.LIBRARIES = ["places"];
}

export function loadGoogle() {
	if (typeof window !== "undefined") {
		googleLoadPromise = new Promise((resolve) => {
			GoogleMapsLoader.load((google) => resolve(google));
		});
	}
}

export function removeAutocomplete(autoComplete) {
	if (!autoComplete) {
		return Promise.resolve();
	}

	// Clean up observers and event listeners if they exist
	if (autoComplete._pacCleanup) {
		autoComplete._pacCleanup();
		delete autoComplete._pacCleanup;
	}

	return googleLoadPromise
		? googleLoadPromise.then((google) => {
				google.maps.event.clearInstanceListeners(autoComplete);
		  })
		: Promise.resolve();
}

export function clearAutocomplete(autoComplete) {
	removeAutocomplete(autoComplete);
	autoComplete.removeAttribute("placeholder"); //	oops something went wrong
	autoComplete.removeAttribute("disabled"); //	user can type
	autoComplete.removeAttribute("style"); //	little warning icon
}

export function createAutocomplete(element, onPlaceChange) {
	// Load Google if not already loaded
	if (!googleLoadPromise) {
		loadGoogle();
	}

	elements.push(element);

	window.gm_authFailure = () => {
		elements.forEach((theElement) => {
			clearAutocomplete(theElement);
		});
	};

	return googleLoadPromise.then((theGoogs) => {
		const options = {
			types: ["geocode"],
			componentRestrictions: {
				country: "us"
			},
			fields: [
				"place_id",
				"formatted_address",
				"address_components",
				"geometry"
			]
		};
		const autocomplete = new theGoogs.maps.places.Autocomplete(
			element,
			options
		);

		// Fix autocomplete dropdown width and position to match the prefix wrapper if it exists
		const prefixWrapper = element.closest && element.closest(".input-prefix-wrapper");
		if (prefixWrapper) {
			let styleObserver = null;
			let isAdjusting = false;

			const adjustDropdownPosition = () => {
				const pacContainer = document.querySelector(".pac-container");
				if (pacContainer && prefixWrapper && pacContainer.style.display !== "none") {
					isAdjusting = true;

					const wrapperRect = prefixWrapper.getBoundingClientRect();
					const wrapperWidth = wrapperRect.width;
					const wrapperLeft = wrapperRect.left + window.scrollX;

					// Set width to match wrapper
					const targetWidth = `${wrapperWidth}px`;
					if (pacContainer.style.width !== targetWidth) {
						pacContainer.style.width = targetWidth;
					}

					// Set left position to align with wrapper's left edge
					const targetLeft = `${wrapperLeft}px`;
					if (pacContainer.style.left !== targetLeft) {
						pacContainer.style.left = targetLeft;
					}

					isAdjusting = false;
				}
			};

			// Watch for pac-container to be added to the DOM
			const bodyObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node.classList && node.classList.contains("pac-container")) {
							adjustDropdownPosition();

							// Watch for style changes on the pac-container
							if (!styleObserver) {
								styleObserver = new MutationObserver(() => {
									if (!isAdjusting) {
										adjustDropdownPosition();
									}
								});

								styleObserver.observe(node, {
									attributes: true,
									attributeFilter: ["style"]
								});
							}
						}
					});
				});
			});

			bodyObserver.observe(document.body, {
				childList: true,
				subtree: false
			});

			// Initial adjustment on focus
			const focusHandler = () => {
				setTimeout(adjustDropdownPosition, 10);
			};
			element.addEventListener("focus", focusHandler);

			// Store cleanup function on the autocomplete instance for later removal
			autocomplete._pacCleanup = () => {
				bodyObserver.disconnect();
				if (styleObserver) {
					styleObserver.disconnect();
				}
				element.removeEventListener("focus", focusHandler);
			};
		}

		theGoogs.maps.event.addDomListener(element, "keydown", (event) => {
			// hitting enter to accept an auto complete answer will also trigger
			// form submission before the callback can update state.
			// for that reason, we look to see if the drawer is open, and
			// if enter is pressed (keycode 13) too, we prevent submitting the form.
			// the next time enter is pressed to submit the form the drawer is closed.

			const autoCompleteDrawerOpen = document.querySelectorAll(
				".pac-container .pac-item"
			).length;
			if (event.keyCode === 13 && autoCompleteDrawerOpen) {
				event.preventDefault();
			}
		});

		theGoogs.maps.event.addListener(autocomplete, "place_changed", () => {
			const place = autocomplete.getPlace();
			// place_changed is triggered when user presses enter (even though autocomplete wasn't used)
			// To prevent this from creating a change event, we can check for a place_id which won't exist
			// if enter was pressed.
			if (typeof place.place_id !== "undefined") {
				onPlaceChange(place.formatted_address, place);
			}
		});

		return autocomplete;
	});
}

export function formatPlaceObject(place) {
	const params = {};

	if (place) {
		const addressComponents = place.address_components.reduce(
			(components, c) => ({
				...components,
				[head(c.types)]: c.short_name
			}),
			{}
		);

		params.city = addressComponents.locality;
		params.zip = addressComponents.postal_code;
		params.state = addressComponents.administrative_area_level_1;
		params.locationSource = "google_autocomplete";

		if (place.geometry) {
			params.latitude = place.geometry.location.lat();
			params.longitude = place.geometry.location.lng();
		}
	}
	return params;
}
